import { request } from './index'
import $store from '@/store/index'
import { resolveTagMatchMode } from '@/assets/js/handleTag'

const protocol = window.location.protocol

// 支持传入额外 axios 配置（如 AbortController signal） [Claude Code]
export const getSourceList = (data, extraConfig = {}) => {
  return request({
    url: '/media-hub/source/sourceListFromSearch',
    method: 'POST',
    // 随组设置 tagFilterMode 发送多 tag 匹配模式（MH-3459）：axios 层统一注入，所有调用宿主无感生效 [Claude Code]
    data: { ...data, tagMatchMode: resolveTagMatchMode($store.state.adminInfo.tagFilterMode) },
    notifyTitle: 'Get Sources',
    ...extraConfig
  })
}

/**
 * 根据 objectId 获取单个 source 对象，用于编辑后的列表局部更新，避免全量调用 list 接口 [Claude Code]
 *
 * @param {Object} data - 请求参数
 * @param {string} data.objectId - source object id（必填）
 *
 * @example
 * getSourceByObjectId({ objectId: 'obj-001' }).then(res => {
 *   if (res.errorCode === '0x0') console.log(res.result)
 * })
 *
 * @returns {Promise}
 * 成功响应示例：
 * {
 *   errorCode: '0x0',                       // 0x0 = success
 *   errorInfo: 'success',                   // error message
 *   result: {
 *     objectId: 'obj-001',                  // source object id
 *     objectName: 'My Camera',              // source object name
 *     objectType: 1,                        // object type code
 *     objectTypeStr: 'source',              // object type string
 *     tangibleId: 'TM1234',                 // tangible (underlying device/stream) id
 *     tangibleName: 'TVU One',              // tangible name
 *     tangibleType: 'X',                    // tangible type (X / T / ...)
 *     updateTimestamp: 1716700000000,       // update timestamp
 *     createTimestamp: 1716700000000,       // create timestamp
 *     tags: [{                              // tag list
 *       tagId: 'tag-001',                   // tag id
 *       tagName: 'Live',                    // tag name
 *       label: 'label-1',                   // label
 *       classification: 1                   // classification
 *     }],
 *     status: 1,                            // device status (1=online)
 *     description: 'Field camera in studio A', // description
 *     remark: 'Field camera in studio A',   // remark (mirrors description)
 *     concreteType: 'TVU_ONE',              // concrete device type
 *     previewId: 'preview-001',             // preview id
 *     rtilCode: 'rtil-abc',                 // rtil code
 *     partyCode: 'party-001',               // party code
 *     previewType: 1,                       // preview type
 *     url: 'rtmp://example.com/live',       // device / stream url
 *     previewStatus: 1,                     // preview status
 *     previewStartTime: 1716700000000,      // preview start timestamp
 *     videoUrl: 'https://example.com/video.m3u8', // video url
 *     recordStatus: 0,                      // record status
 *     taskId: 'task-001',                   // task id
 *     objectConnectInfo: {                  // 连接信息
 *       status: 'active',                   // source object status (inactive | active)
 *       live: true,                         // whether the source is LIVE
 *       projectId: 'project-001'            // when source object is live this value will be returned
 *     },
 *     attributes: [{                        // extra attributes
 *       attrName: 'codec',                  // attribute name
 *       attrValue: ['H264']                 // attribute values
 *     }],
 *     sourceAttrDTO: {                      // source 属性 DTO
 *       id: 'attr-001',                     // primary key
 *       createTime: 1716700000000,          // create time
 *       updateTime: 1716700000000,          // update time
 *       deleteFlag: 0,                      // delete flag
 *       bitrate: 4000,                      // bitrate
 *       delay: 1000,                        // delay (ms)
 *       sourceId: 'src-001',                // source id
 *       objectId: 'obj-001',                // object id
 *       region: 'us-east-1',                // region
 *       boxPoolId: 'pool-001'               // box pool id
 *     },
 *     extraInfo: { url: 'rtmp://...' },     // extra info map
 *     rank: 9999,                           // sort rank (default 9999)
 *     env: 'prod'                           // environment
 *   }
 * }
 *
 * 失败响应示例：
 * { errorCode: '0x1', errorInfo: 'error message' }
 */
export const getSourceByObjectId = (data) => {
  return request({
    url: '/media-hub/source/getByObjectId',
    method: 'GET',
    params: data,
    notifyTitle: 'Get Source'
  })
}

export const createProfile = (data) => {
  return request({
    url: '/media-hub/project/addOutputProject',
    method: 'POST',
    data,
    notifyTitle: 'Create Profile'
  })
}

export const getProfileList = (data) => {
  return request({
    url: '/media-hub/project/listOutputProject',
    method: 'POST',
    // 随组设置 tagFilterMode 发送多 tag 匹配模式（MH-3459）：destinations 子窗 / outputModule 的列表走此端点，
    // 后端契约（MH-3458 评论 263979）明确 listOutputProject 支持 tagMatchMode [Claude Code]
    data: { ...data, tagMatchMode: resolveTagMatchMode($store.state.adminInfo.tagFilterMode) },
    notifyTitle: 'Get Profiles'
  })
}

export const updateProfile = (data) => {
  return request({
    url: '/media-hub/project/updateOutputProject',
    method: 'POST',
    data,
    notifyTitle: 'Update Profile'
  })
}

export const deleteProfile = (data) => {
  return request({
    url: '/media-hub/project/deleteOutputProject',
    method: 'GET',
    params: data,
    notifyTitle: 'Delete Profile'
  })
}

export const startLiveProfile = (data) => {
  return request({
    url: '/media-hub/project/startLive',
    method: 'POST',
    // wsNotifyKey 复合格式 `<action>_<projectId>`，WS 端解析 key 拿到 projectId [Claude Code]
    data: { ...data, wsNotifyKey: `projectStartLive_${data.projectId}` },
    notifyTitle: 'Start Live'
  })
}

export const switchProfile = (data) => {
  return request({
    url: '/media-hub/project/switch',
    method: 'POST',
    // wsNotifyKey 复合格式 `<action>_<projectId>`，WS 端解析 key 拿到 projectId [Claude Code]
    data: { ...data, wsNotifyKey: `projectSwitchLive_${data.projectId}` },
    notifyTitle: 'Switch Profile'
  })
}

export const stopLiveProfile = (data) => {
  return request({
    url: '/media-hub/project/stopLive',
    method: 'GET',
    // wsNotifyKey 复合格式 `<action>_<projectId>`；GET 接口需扩展到 params 才能透传 [Claude Code]
    params: { ...data, wsNotifyKey: `projectStopLive_${data.projectId}` },
    notifyTitle: 'Stop Live'
  })
}

export const checkLoadingStatus = (data) => {
  return request({
    url: '/media-hub/project/loadingInfo',
    method: 'POST',
    data,
    notifyTitle: 'Check Loading Status'
  })
}

export const getPartyCode = (data) => {
  return request({
    url: `${protocol}//${$store.state.initConfig.gatewayUrl}/device-extra/rtil/query-and-join`,
    method: 'POST',
    data,
    notifyTitle: 'Get Party Code'
  })
}

export const joinParty = (data) => {
  return request({
    url: `${protocol}//${$store.state.initConfig.gatewayUrl}/device-extra/rtil/join`,
    method: 'POST',
    data,
    notifyTitle: 'Join Party'
  })
}

export const joinRTILParty = (data) => {
  return request({
    url: `${protocol}//${$store.state.initConfig.gatewayUrl}/rtil/party/joinParty`,
    method: 'POST',
    data,
    notifyTitle: 'Join RTIL Party'
  })
}

export const queryTag = (data) => {
  return request({
    url: '/media-hub/sourceTag/sourceTagListFromSearch',
    method: 'GET',
    params: data,
    notifyTitle: 'Get Tags'
  })
}

export const deleteSource = (data) => {
  return request({
    url: `/media-hub/source/objectDelete?objectId=${data.objectId}`,
    method: 'GET',
    notifyTitle: 'Delete Source'
  })
}

export const queryOutputTag = (data) => {
  return request({
    url: '/media-hub/project/getTags',
    method: 'GET',
    params: data,
    notifyTitle: 'Get Output Tags'
  })
}

export const projectProfileBind = (data) => {
  return request({
    url: '/media-hub/project/bind/destination',
    method: 'POST',
    data,
    notifyTitle: 'Bind Destination'
  })
}

export const projectProfileUnBind = (data) => {
  return request({
    url: '/media-hub/project/unbind/destination',
    method: 'POST',
    data,
    notifyTitle: 'Unbind Destination'
  })
}

export const queryBindOutput = (data) => {
  return request({
    url: '/media-hub/profile/active/list',
    method: 'GET',
    params: data,
    notifyTitle: 'Get Bound Outputs'
  })
}

export const querySourceInfo = (data) => {
  return request({
    url: '/media-hub/source/getById',
    method: 'GET',
    params: data,
    notifyTitle: 'Get Source Info'
  })
}

export const updateSourceInfo = (data) => {
  return request({
    url: '/media-hub/source/updateSource',
    method: 'POST',
    data,
    notifyTitle: 'Update Source'
  })
}

export const getProfile = (data) => {
  return request({
    url: '/media-hub/project/getOutputProject',
    method: 'GET',
    params: data,
    notifyTitle: 'Get Profile'
  })
}

export const getDomain = (data) => {
  return request({
    url: '/media-hub/receiver/getDomain',
    method: 'GET',
    params: data,
    notifyTitle: 'Get Domain'
  })
}

// 获取最优region
export const getRegion = (data) => {
  return request({
    url: '/media-hub/receiver/chooseRegion',
    method: 'GET',
    params: data,
    notifyTitle: 'Get Region'
  })
}

// get Region List
export const getRegionList = (data) => {
  return request({
    url: '/media-hub/receiver/listActiveRegion',
    method: 'GET',
    params: data,
    notifyTitle: 'Get Region List'
  })
}

export const getCascaderRegionList = (data) => {
  return request({
    url: '/media-hub/receiver/listCloudPlatforms',
    method: 'GET',
    params: data,
    notifyTitle: 'Get Cloud Platforms'
  })
}

export const checkProjectStatus = (data) => {
  return request({
    url: '/media-hub/project/status',
    method: 'POST',
    data,
    notifyTitle: 'Check Project Status'
  })
}

export const getPhysicalList = () => {
  return request({
    // ${ protocol }//${$store.state.initConfig.homeDomain}/
    url: `${protocol}//${$store.state.initConfig.homeDomain}/home-service/boxPool/page?condition=&pageNum=1&pageSize=999`,
    method: 'GET',
    notifyTitle: 'Get Physical List'
  })
}

export const getboxListByPool = (poolId) => {
  return request({
    url: `${protocol}//${$store.state.initConfig.homeDomain}/home-service/agent/v2/page?poolId=${poolId}&condition=&pageNum=1&pageSize=999`,
    method: 'GET'
  })
}

export const getPullURL = (data) => {
  return request({
    url: '/media-hub/project/input/taskInfo',
    method: 'GET',
    params: data,
    notifyTitle: 'Get Pull URL'
  })
}

export const startPreview = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/preview/start`,
    method: 'POST',
    data,
    notifyTitle: 'Start Preview'
  })
}

export const stopPreview = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/preview/stop`,
    method: 'POST',
    data,
    notifyTitle: 'Stop Preview'
  })
}

export const existTask = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/preview/existTask`,
    method: 'GET',
    params: data,
    notifyTitle: 'Check Preview Task'
  })
}

export const checkLoginInfo = (data) => {
  return request({
    url: '/media-hub/base/loginUserInfo',
    method: 'GET',
    params: data,
    notifyTitle: 'Get Login Info'
  })
}

export const getStaticIP = (region) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/common/staticIpInfos?region=${region}`,
    method: 'GET',
    notifyTitle: 'Get Static IP'
  })
}

export const updateStaticIP = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/common/updateObjIp`,
    method: 'POST',
    params: data,
    notifyTitle: 'Update Static IP'
  })
}

export const getSDIList = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/common/sdi/device`,
    method: 'GET',
    params: data,
    notifyTitle: 'Get SDI List'
  })
}

export const queryEncodingProfile = (profileId) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/profile/queryEncodingProfile?profileId=${profileId}`,
    method: 'GET',
    notifyTitle: 'Get Encoding Profile'
  })
}

export const setDefaultSource = (data) => {
  return request({
    url: '/media-hub/source/addDefaultObject',
    method: 'POST',
    data,
    notifyTitle: 'Set Default Source'
  })
}

export const getDeviceConfig = (objectId) => {
  return request({
    url: `/media-hub/source/attr/setting?objectId=${objectId}`,
    method: 'GET',
    notifyTitle: 'Get Device Config'
  })
}

export const saveDeviceConfig = (data) => {
  return request({
    url: '/media-hub/source/attr/setting',
    method: 'POST',
    data,
    notifyTitle: 'Save Device Config'
  })
}

export const getGroupSetting = (groupId, keys) => {
  return request({
    url: `/media-hub/admin/groupSettings?groupId=${groupId}&keys=${keys}`,
    method: 'GET',
    notifyTitle: 'Get Group Setting'
  })
}

export function setGroupSetting (data) {
  return request({
    url: '/media-hub/admin/groupSetting',
    method: 'POST',
    data,
    notifyTitle: 'Save Group Setting'
  })
}

export const activeSDIDevice = (boxId) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/common/activeBox?boxId=${boxId}`,
    method: 'GET',
    disabledErrorTips: true,
    notifyTitle: 'Activate SDI Device'
  })
}

export const getAgentContent = () => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/common/agent/installer`,
    method: 'GET',
    notifyTitle: 'Get Agent Installer'
  })
}

export const getWhitebalance = (businessId, outputId) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/common/details/whitebalance?businessId=${businessId}&outputId=${outputId}`,
    method: 'GET',
    notifyTitle: 'Get White Balance'
  })
}

export const setWhitebalance = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/common/whitebalance`,
    method: 'POST',
    data,
    notifyTitle: 'Set White Balance'
  })
}

export const setRGB = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/common/rgb/whitebalance`,
    method: 'POST',
    data,
    notifyTitle: 'Set RGB'
  })
}

export const setReferenceImage = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/common/whitebalance/setReferenceImage`,
    method: 'POST',
    data,
    notifyTitle: 'Set Reference Image'
  })
}

export const clearReferenceImage = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/common/whitebalance/cleanReferenceImage`,
    method: 'POST',
    data,
    notifyTitle: 'Clear Reference Image'
  })
}

export const stopRecord = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/record/stop`,
    method: 'POST',
    data,
    notifyTitle: 'Stop Record'
  })
}

export const startRecord = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/record/start`,
    method: 'POST',
    data,
    notifyTitle: 'Start Record'
  })
}

export const getProjectIdByObjectId = (data) => {
  return request({
    url: '/media-hub/project/getProjectIdByObjectId',
    method: 'GET',
    params: data,
    notifyTitle: 'Get Project ID'
  })
}

export const updateProjectAttr = (data) => {
  return request({
    url: '/media-hub/project/updateProjectAttr',
    method: 'GET',
    params: data,
    notifyTitle: 'Update Project'
  })
}

export const getUserMode = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/rgb/user/mode/tags`,
    method: 'GET',
    params: data,
    notifyTitle: 'Get User Mode'
  })
}
export const setUserModeTag = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/rgb/user/mode/tag`,
    method: 'POST',
    data,
    notifyTitle: 'Set User Mode Tag'
  })
}

export const updateModeUrl = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/rgb/mode`,
    method: 'POST',
    data,
    notifyTitle: 'Update Mode'
  })
}

export const getRGBWhitebalance = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/rgb/whitebalance/detail`,
    method: 'GET',
    params: data,
    notifyTitle: 'Get RGB White Balance'
  })
}

export const adjustByImage = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/rgb/whitebalance/adjustByImage`,
    method: 'POST',
    data,
    notifyTitle: 'Adjust By Image'
  })
}

export const resetWhitebalance = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/rgb/whitebalance/reset`,
    method: 'POST',
    data,
    notifyTitle: 'Reset White Balance'
  })
}

export const setWhitebalanceManual = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/rgb/whitebalance/adjust`,
    method: 'POST',
    data,
    notifyTitle: 'Adjust White Balance'
  })
}

export const setModeComment = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/rgb/mode/comment`,
    method: 'GET',
    params: data,
    notifyTitle: 'Set Mode Comment'
  })
}

export const getMAStatus = (data) => {
  return request({
    url: `${$store.state.initConfig.maPath}/scope/noc/checkCurrentUrl`,
    method: 'GET',
    params: data,
    notifyTitle: 'Get MA Status'
  })
}

/**
 * 通知 Media Analyzer 按 objectId 开始解析（MH-3050）[Claude Code]
 * 来源 Apifox: POST /analyzer/object (api-471677442)
 *
 * 由 MH 后端将用户组配置的 tvuRegion 转换为 region + vendor 后转发给 MA，
 * 替代旧的 `${maPath}/scope/noc/start`（旧接口不带资源配置，MA 会落到 AWS 默认 region）[Claude Code]
 *
 * @param {Object} data - 请求参数（作为 query string 传递）
 * @param {string} data.objectId - 要解析的 object id（必填）
 *
 * @example
 * notifyMA({ objectId: 'obj-1001' }).then(res => {
 *   if (res.errorCode === '0x0') {
 *     // MA 解析任务已启动
 *   }
 * })
 *
 * @returns {Promise}
 * 成功响应示例：
 * {
 *   errorCode: '0x0',     // 返回错误码
 *   errorInfo: 'success', // 返回信息描述
 *   async: false,         // 是否异步
 *   result: {}            // 返回数据（media-analyzer 返回的 scope 结果）
 * }
 *
 * 失败响应示例：
 * {
 *   errorCode: '0x1',
 *   errorInfo: 'error message'
 * }
 */
export const notifyMA = (data) => {
  return request({
    url: '/media-hub/analyzer/object',
    method: 'POST',
    params: data,
    notifyTitle: 'Notify MA'
  })
}

export const clearAiApply = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/rgb/clearAiApply`,
    method: 'GET',
    params: data,
    notifyTitle: 'Clear AI Apply'
  })
}

export const mousePressEvent = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/decoder/mouse/press`,
    method: 'POST',
    data,
    notifyTitle: 'Mouse Press'
  })
}

export const mouseReleaseEvent = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/decoder/mouse/release`,
    method: 'POST',
    data,
    notifyTitle: 'Mouse Release'
  })
}

export const mouseMoveEvent = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/decoder/mouse/move`,
    method: 'POST',
    data,
    notifyTitle: 'Mouse Move'
  })
}

export const keyboardPressEvent = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/decoder/keyboard/press`,
    method: 'POST',
    data,
    notifyTitle: 'Keyboard Press'
  })
}

export const keyboardReleaseEvent = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/decoder/keyboard/release`,
    method: 'POST',
    data,
    notifyTitle: 'Keyboard Release'
  })
}

export const mouseWheelEvent = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/decoder/mouse/wheel`,
    method: 'POST',
    data,
    notifyTitle: 'Mouse Wheel'
  })
}

export const initBrowser = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/decoder/initBrowser`,
    method: 'POST',
    data,
    notifyTitle: 'Init Browser'
  })
}

export const switchWebPage = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/decoder/switchWebPage`,
    method: 'POST',
    data,
    notifyTitle: 'Switch Web Page'
  })
}

export const closeWebPage = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/decoder/closeWebPage`,
    method: 'POST',
    data,
    notifyTitle: 'Close Web Page'
  })
}

export const getBrowserInfo = (processId) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/decoder/getBrowserStatus?processId=${processId}`,
    method: 'GET',
    notifyTitle: 'Get Browser Info'
  })
}

export const goForward = (processId) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/decoder/goForward?processId=${processId}`,
    method: 'GET',
    notifyTitle: 'Go Forward'
  })
}

export const reFresh = (processId) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/decoder/reload?processId=${processId}`,
    method: 'GET',
    notifyTitle: 'Refresh Page'
  })
}

export const goBack = (processId) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/decoder/goBack?processId=${processId}`,
    method: 'GET',
    notifyTitle: 'Go Back'
  })
}

export const zoomReset = (processId) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/decoder/zoomReset?processId=${processId}`,
    method: 'GET',
    notifyTitle: 'Zoom Reset'
  })
}

export const zoomIn = (processId) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/decoder/zoomIn?processId=${processId}`,
    method: 'GET',
    notifyTitle: 'Zoom In'
  })
}

export const zoomOut = (processId) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/decoder/zoomOut?processId=${processId}`,
    method: 'GET',
    notifyTitle: 'Zoom Out'
  })
}

export const sourceByRank = (data) => {
  return request({
    url: '/media-hub/source/rank',
    method: 'POST',
    data,
    notifyTitle: 'Rank Sources'
  })
}

export const updateBitrateAndDelay = (data) => {
  return request({
    // url: `${protocol}//${$store.state.initConfig.ccUrl}/ccp/tvucc-device/receiver/setBitrateAndDelay`,
    url: `${protocol}//${$store.state.initConfig.ccUrl}/ccp/tvucc-rws/receiver/command/setBitrateAndDelay`,
    method: 'POST',
    data,
    requestBase: 'FORM',
    // disabledErrorTips: true
    notifyTitle: 'Update Bitrate'
  })
}

export const updateMode = (data) => {
  return request({
    url: `${protocol}//${$store.state.initConfig.ccUrl}/ccp/tvucc-rws/receiver/command/setCbrVbr`,
    method: 'POST',
    data,
    requestBase: 'FORM',
    // disabledErrorTips: true
    notifyTitle: 'Update Mode'
  })
}

export const updateDynamicOrFix = (data) => {
  return request({
    url: `${protocol}//${$store.state.initConfig.ccUrl}/ccp/tvucc-rws/receiver/command/setVideoDynamic`,
    method: 'POST',
    data,
    requestBase: 'FORM',
    // disabledErrorTips: true
    notifyTitle: 'Update Video Dynamic'
  })
}

export const setSlotIndex = (data) => {
  return request({
    url: `${protocol}//${$store.state.initConfig.ccUrl}/ccp/tvucc-rws/receiver/command/setSlotIndex`,
    method: 'POST',
    data,
    requestBase: 'FORM',
    disabledErrorTips: true,
    notifyTitle: 'Set Slot Index'
  })
}

export const setSlotReset = (data) => {
  return request({
    url: `${protocol}//${$store.state.initConfig.ccUrl}/ccp/tvucc-rws/receiver/command/setSlotReset`,
    method: 'POST',
    data,
    requestBase: 'FORM',
    disabledErrorTips: true,
    notifyTitle: 'Reset Slot'
  })
}

export const setSlotMode = (data) => {
  return request({
    url: `${protocol}//${$store.state.initConfig.ccUrl}/ccp/tvucc-rws/receiver/command/setSlotMode`,
    method: 'POST',
    data,
    requestBase: 'FORM',
    disabledErrorTips: true,
    notifyTitle: 'Set Slot Mode'
  })
}

export const getUserBehavior = (data) => {
  return request({
    url: `${protocol}//${$store.state.initConfig.ccUrl}/ccp/tvucc-user/userBehavior/get`,
    method: 'GET',
    params: data,
    disabledErrorTips: true,
    notifyTitle: 'Get User Behavior'
  })
}

export const setUserBehavior = (data) => {
  return request({
    url: `${protocol}//${$store.state.initConfig.ccUrl}/ccp/tvucc-user/userBehavior/set`,
    method: 'POST',
    data,
    requestBase: 'FORM',
    disabledErrorTips: true,
    notifyTitle: 'Save User Behavior'
  })
}
export const updateBackup = data => {
  return request({
    url: `/media-hub/project/backUpSource?primaryObjectId=${data.primaryObjectId}&backupObjectId=${data.primaryObjectId}`,
    method: 'POST',
    notifyTitle: 'Update Backup'
  })
}

export const startMAScope = data => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/common/startScopeByObjectId`,
    method: 'GET',
    params: data,
    notifyTitle: 'Start MA Scope'
  })
}

export const getSwitchIdByObject = data => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/common/getSwitcherInfoByObjectId`,
    method: 'GET',
    params: data,
    notifyTitle: 'Get Switcher Info'
  })
}

/// mx-service/common/copyInfoByObject
export const copyInfoByObject = data => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/common/copyInfoByObject`,
    method: 'GET',
    params: data,
    notifyTitle: 'Copy Info'
  })
}

// /media-hub/{project|destination}/preview/change
export const outputPreview = data => {
  return request({
    url: '/media-hub/project/preview/change',
    method: 'GET',
    params: data,
    notifyTitle: 'Preview'
  })
}

export const activeUserInfo = (data) => {
  return request({
    url: `${$store.state.initConfig.mxServiceUrl}/mx-service/common/userLiveInfo`,
    method: 'GET',
    params: data,
    notifyTitle: 'Get Live Info'
  })
}

// get tvu Region List
export const getTvuRegionList = (data) => {
  return request({
    url: '/media-hub/cloud/resource/regionMappings',
    method: 'GET',
    params: data,
    notifyTitle: 'Get Region List'
  })
}

// get default vendor and region
export const getDefaultRegionMapping = (data) => {
  return request({
    url: '/media-hub/cloud/resource/regionMapping/default',
    method: 'GET',
    params: data,
    notifyTitle: 'Get Default Region'
  })
}
export const getTvuRegionByRegion = (region) => {
  return request({
    url: `/media-hub/cloud/resource/regionMapping/region/${region}`,
    method: 'GET',
    notifyTitle: 'Get Region'
  })
}

export const getAudioMxTaskId = (data) => {
  return request({
    url: '/media-hub/destination/getAudioMxTaskId',
    method: 'GET',
    params: data,
    notifyTitle: 'Get Audio Task'
  })
}

/**
 * 获取 Destination 对象列表（分页、筛选、排序） [Claude Code]
 *
 * @param {Object} data - 请求参数
 * @param {string} [data.nickName] - 按名称关键词筛选
 * @param {string} [data.secondType] - 二级类型筛选
 * @param {string} [data.importType] - 导入类型筛选
 * @param {number} [data.pageNum=1] - 页码
 * @param {number} [data.pageSize=15] - 每页条数
 * @param {boolean} [data.requireObject=true] - 是否返回 object 详细信息
 * @param {Object} [data.orderItem] - 排序配置
 * @param {string} [data.orderItem.key] - 排序字段，如 'nickName'
 * @param {boolean} [data.orderItem.desc=false] - 是否降序
 *
 * @example
 * getDestinationObjectList({
 *   nickName: 'rtmp',
 *   pageNum: 1,
 *   pageSize: 15,
 *   requireObject: true,
 *   orderItem: { key: 'nickName', desc: false }
 * }).then(res => {
 *   console.log(res.result.list)
 * })
 *
 * @returns {Promise}
 * 成功响应示例：
 * {
 *   errorCode: '0x0',
 *   errorInfo: 'success',
 *   result: {
 *     total: 10,
 *     list: [{ objectId: 'xxx', objectName: 'xxx', tangibleId: 'xxx', ... }],
 *     pageNum: 1,
 *     pageSize: 15
 *   }
 * }
 *
 * 失败响应示例：
 * {
 *   errorCode: '0x1',
 *   errorInfo: 'error message'
 * }
 */
export const getDestinationObjectList = (data) => {
  return request({
    url: '/media-hub/destination/list',
    method: 'POST',
    data,
    notifyTitle: 'Get Destinations'
  })
}

/**
 * 启动 Destination 直播（将源推流到指定 destination） [Claude Code]
 *
 * @param {Object} data - 请求参数
 * @param {string} data.sourceObjectId - 源对象 ID
 * @param {string} data.destinationObjectId - Destination 对象 ID
 * @param {string} data.appName - 应用名称，如 'MediaHub'
 * @param {string} [data.businessId] - 业务 ID，可填 projectId（可选）
 * @param {number} [data.previewType] - 预览类型，如 2（可选）
 *
 * @example
 * startDestinationLive({
 *   sourceObjectId: '1415640618469822464',
 *   destinationObjectId: '1417526727718080512',
 *   appName: 'MediaHub',
 *   businessId: '',
 *   previewType: 2
 * }).then(res => {
 *   console.log(res.result.graphInfo.graphId)
 * })
 *
 * @returns {Promise}
 * 成功响应示例：
 * {
 *   errorCode: '0x0',
 *   errorInfo: 'success',
 *   async: false,
 *   result: {
 *     outputInfo: { objectId: 'xxx', objectName: 'HLS Pull ...' },
 *     sourceInfo: {
 *       objectId: 'xxx', objectName: 'xxx',
 *       tangibleId: 'xxx', tangibleType: 'Ext',
 *       primary: { objectId: 'xxx', objectName: 'xxx' },
 *       backUp: { objectId: 'xxx', objectName: 'xxx' },
 *       previewBO: { previewId: null, rtilCode: '40171', partyCode: '832283595384' }
 *     },
 *     graphInfo: { graphId: '01K5AZR2M30WD0PM3BVNPS9TSQ' }
 *   }
 * }
 *
 * 失败响应示例：
 * {
 *   errorCode: '0x1',
 *   errorInfo: 'error message'
 * }
 */
export const startDestinationLive = (data) => {
  return request({
    url: '/media-hub/destination/startLive',
    method: 'POST',
    data,
    notifyTitle: 'Start Live'
  })
}

/**
 * 停止目标直播 [Claude Code]
 *
 * @param {Object} data - 请求参数
 * @param {Object} data.loginUser - 当前登录用户信息（可选）
 * @param {string} data.loginUser.id - 用户 ID
 * @param {string} data.loginUser.email - 邮箱
 * @param {string} data.loginUser.role - 角色
 * @param {Object} data.liveDTO - 直播参数
 * @param {string} data.liveDTO.sourceObjectId - 源对象 ID（必填）
 * @param {string} data.liveDTO.destinationObjectId - 目标对象 ID（必填）
 * @param {string} data.liveDTO.appName - 应用名称（必填）
 * @param {string} [data.liveDTO.userId] - 用户 ID
 * @param {string} [data.liveDTO.email] - 邮箱
 * @param {string} [data.liveDTO.businessId] - 业务 ID
 * @param {boolean} [data.liveDTO.enablePreview] - 是否启用预览
 * @param {number} [data.liveDTO.previewType] - 预览类型
 * @param {Object} [data.liveDTO.graphMetadata] - 图元数据（key-value）
 *
 * @example
 * stopDestinationLive({
 *   liveDTO: {
 *     sourceObjectId: 'source-xxx',
 *     destinationObjectId: 'dest-xxx',
 *     appName: 'MediaHub'
 *   }
 * }).then(res => {
 *   console.log(res.result)
 * })
 *
 * @returns {Promise}
 * 成功响应示例：
 * {
 *   errorCode: '0x0',
 *   errorInfo: 'success',
 *   async: false,
 *   result: {}
 * }
 *
 * 失败响应示例：
 * {
 *   errorCode: '0x1',
 *   errorInfo: 'error message'
 * }
 */
export const stopDestinationLive = (data) => {
  return request({
    url: '/media-hub/destination/stopLive',
    method: 'POST',
    data,
    notifyTitle: 'Stop Live'
  })
}

/**
 * 根据 objectId 获取 project 详情（含绑定的 destinations 列表） [Claude Code]
 *
 * @param {Object} params - 请求参数
 * @param {string} params.objectId - project 的 objectId
 *
 * @example
 * getProjectByObjectId({ objectId: '1478454456185131008' }).then(res => {
 *   console.log(res.result.objectName)
 *   console.log(res.result.destinations)
 * })
 *
 * @returns {Promise}
 * 成功响应示例：
 * {
 *   errorCode: '0x0',
 *   errorInfo: 'success',
 *   result: {
 *     objectId: '1478454456185131008',        // search 层对象 ID
 *     objectName: 'andyProject->D',            // project 名称
 *     tangibleId: '61eea2b9c58949a6aec053a3be16e99c', // projectId（底层资源 ID）
 *     tangibleName: 'andyProject->D',          // project 名称（同 objectName）
 *     tangibleType: 'project',                 // 固定为 project
 *     objectType: 15,                          // 固定为 15（PROJECT）
 *     updateTimestamp: 1718000000000,           // 最后更新时间戳（毫秒）
 *     description: '',                         // 描述
 *     remark: '',                              // 备注
 *     previewId: '',                           // 预览 ID，用于 RTIL 预览
 *     rtilCode: '',                            // RTIL Code
 *     partyCode: '',                           // Party Code
 *     url: '',                                 // 推流地址
 *     extraInfo: {},                           // 扩展参数
 *     tags: [{ tagName: '', classification: '' }],           // 标签列表
 *     attributes: [{ attrName: '', attrValue: [''] }],      // 对象属性列表（如 tvuRegion、boxPoolId）
 *     tangibleInfo: null,                      // 底层资源详情（project 根节点此项为 null）
 *     living: false,                           // 当前是否有任意 destination 正在直播
 *     liveTime: 0,                             // 直播开始时间戳（毫秒），未直播时为 0
 *     destinations: [{                         // 绑定的 destination 列表
 *       objectId: '1478454340338454528',        // search 层对象 ID
 *       objectName: 'SRT Listener 2025/12/18 16:54', // 对象名称
 *       tangibleId: 'bb2e7c09b6e341c58c8cae105760c692', // 底层资源 ID（mediaId）
 *       tangibleName: '',                       // 底层资源名称
 *       tangibleType: 'SRTLISTENER',            // 底层资源类型（SRTLISTENER / RTMPPULL / SRT 等）
 *       updateTimestamp: 1718000000000,          // 最后更新时间戳（毫秒）
 *       description: '',                        // 描述
 *       remark: '',                             // 备注
 *       previewId: '',                          // 预览 ID
 *       rtilCode: '',                           // RTIL Code
 *       partyCode: '',                          // Party Code
 *       url: 'srt://host:47087?mode=listener&latency=120000', // 输出推流地址
 *       extraInfo: {},                          // 扩展参数（含 domainKey、port、streamKey、latency 等）
 *       attributes: [{ attrName: '', attrValue: [''] }], // 对象属性列表
 *       tangibleInfo: {}                        // 底层资源详情（来自 mx-service）
 *     }]
 *   }
 * }
 *
 * 失败响应示例：
 * {
 *   errorCode: '0x1',
 *   errorInfo: 'error message'
 * }
 */
export const getProjectByObjectId = (params) => {
  return request({
    url: '/media-hub/project/getProjectByObjectId',
    method: 'GET',
    params,
    notifyTitle: 'Get Project Detail'
  })
}
